import { CreateUserData, UpdateUserData, User } from '@adminStore/users';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  userControllerCreate,
  UserControllerCreate$Params,
  userControllerDelete,
  userControllerFindAll,
  userControllerFindOne,
  userControllerMerge,
  userControllerMergePreview,
  userControllerUpdate,
  UserControllerUpdate$Params,
} from '@klubovna/api-client';
import { MergeUsersPreviewResponse } from '@klubovna/api-client';
import { MergeUsersResponse } from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'email' | 'role' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: UsersQueryParams): Observable<PaginatedResponse<User>> {
    return userControllerFindAll(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => toPaginatedResponse<User>(response.body)));
  }

  findOne(id: string): Observable<User> {
    return userControllerFindOne(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as unknown as User));
  }

  create(userData: CreateUserData): Observable<User> {
    const params: UserControllerCreate$Params = {
      body: userData,
    };
    return userControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as unknown as User));
  }

  update(id: string, userData: UpdateUserData): Observable<User> {
    const params: UserControllerUpdate$Params = {
      id,
      body: userData,
    };
    return userControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as unknown as User));
  }

  delete(id: string): Observable<void> {
    return userControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  mergePreview(sourceUserId: string, targetUserId: string): Observable<MergeUsersPreviewResponse> {
    return userControllerMergePreview(this.http, this.apiConfig.rootUrl, {
      body: { sourceUserId, targetUserId },
    }).pipe(map(response => response.body));
  }

  merge(sourceUserId: string, targetUserId: string, confirmationToken: string): Observable<MergeUsersResponse> {
    return userControllerMerge(this.http, this.apiConfig.rootUrl, {
      body: { sourceUserId, targetUserId, confirmationToken },
    }).pipe(map(response => response.body));
  }
}
